import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { StatusBadge, PriorityBadge } from "../components/badges";
import { FolderKanban, CheckCircle2, AlertCircle, Ban } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
    api.get("/projects").then((r) => setProjects(r.data));
  }, []);

  const cards = stats ? [
    { label: "Active Projects", value: stats.active_projects, icon: FolderKanban, color: "text-blue-500" },
    { label: "Tasks Completed", value: `${stats.completed_tasks}/${stats.total_tasks}`, icon: CheckCircle2, color: "text-emerald-500" },
    { label: "Open Issues", value: stats.open_issues, icon: AlertCircle, color: "text-amber-500" },
    { label: "Blocked Tasks", value: stats.blocked_tasks, icon: Ban, color: "text-rose-500" },
  ] : [];

  return (
    <div className="space-y-8 fade-in" data-testid="dashboard-page">
      <div>
        <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">R&D Command Center</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-1">
          Welcome back, {user?.name?.split(" ")[0]}
        </h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 stagger">
        {cards.map((c) => (
          <Card key={c.label} className="p-5" data-testid={`stat-card-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div className="flex items-center justify-between">
              <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{c.label}</p>
              <c.icon className={`h-4 w-4 ${c.color}`} />
            </div>
            <p className="font-heading text-3xl font-bold mt-2">{c.value}</p>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 space-y-3">
          <h2 className="font-heading text-lg font-semibold">Projects</h2>
          <div className="space-y-3 stagger">
            {projects.map((p) => (
              <Link key={p.id} to={`/projects/${p.id}`} className="block" data-testid={`project-card-${p.key.toLowerCase()}`}>
                <Card className="p-5 hover:border-primary/50 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs text-primary">{p.key}</span>
                        <StatusBadge status={p.status} />
                      </div>
                      <h3 className="font-heading font-semibold mt-1 truncate">{p.name}</h3>
                      <p className="text-sm text-muted-foreground line-clamp-1 mt-0.5">{p.description}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-heading text-xl font-bold">{p.progress}%</p>
                      <p className="text-[10px] font-mono text-muted-foreground uppercase">{p.completed_count}/{p.task_count} tasks · {p.open_issues} issues</p>
                    </div>
                  </div>
                  <Progress value={p.progress} className="h-1.5 mt-4" />
                </Card>
              </Link>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2 space-y-3">
          <h2 className="font-heading text-lg font-semibold">My Open Tasks ({stats?.my_open_tasks ?? 0})</h2>
          <Card className="divide-y divide-border">
            {stats?.my_tasks?.length ? stats.my_tasks.map((t) => (
              <div key={t.id} className="p-4 flex items-center gap-3" data-testid="my-task-item">
                <PriorityBadge priority={t.priority} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{t.title}</p>
                  <p className="text-[10px] font-mono text-muted-foreground">{t.tid} · due {t.due_date || "—"}</p>
                </div>
                <StatusBadge status={t.status} />
              </div>
            )) : (
              <p className="p-6 text-sm text-muted-foreground">No open tasks assigned to you.</p>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
